import { LoadingAnimationBackgroundImage } from 'library/ts/ui/components/loading/LoadingAnimationBackgroundImage';
import { LoadingAnimationSpinner } from 'library/ts/ui/components/loading/LoadingAnimationSpinner';
import { LoadingAnimationTimeoutMessage } from 'library/ts/ui/components/loading/LoadingAnimationTimeoutMessage';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
export class LoadingAnimationManager {
    #enabled = true;
    #config;
    #utils;
    #capabilities;
    #visible = false;
    #loadingAnimationContainer;
    #svgAnimationCSSClass;
    #fadeInEnabled;
    #backgroundImageComponent;
    #spinnerComponent;
    #timeoutMessageComponent;
    #SVG_SPINNER_ANIMATION = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">' +
        '<g style="fill: rgba( 255, 255, 255, 0.8 ) !important;">' +
        '<path d="M8.16,138.48A99.6,99.6,0,0,0,49,185.78l5.8-14.07a84.94,84.94,0,0,1-.27-143.23l-5.83-14A100.06,100.06,0,0,0,8.16,138.48Z"/>' +
        '<path d="M199.54,85.9A100.13,100.13,0,0,0,76.29,3l5.83,14A85,85,0,0,1,184.38,85.9Z"/>' +
        '<path d="M138.94,192.39a100,100,0,0,0,60.34-76.6H184.07A84.86,84.86,0,0,1,82.43,183.1l-5.8,14.07A99.6,99.6,0,0,0,138.94,192.39Z"/>' +
        '</g>' +
        '</svg>';
    constructor(svgAnimationCSSClass = undefined, container = undefined, utils = undefined, capabilities = undefined, enabled = true, config = undefined) {
        this.#svgAnimationCSSClass = svgAnimationCSSClass;
        this.#loadingAnimationContainer = container;
        this.#utils = utils;
        this.#capabilities = capabilities;
        this.#enabled = enabled;
        this.#config = config;
        if (this.#config?.backgroundImage?.enabled && this.#utils.URL.valid(this.#config?.backgroundImage?.imageURL)) {
            this.#backgroundImageComponent = new LoadingAnimationBackgroundImage(this.#loadingAnimationContainer, this.#config?.backgroundImage);
        }
        if (this.#config?.timeoutMessage) {
            const timeoutConfig = {
                enabled: !!this.#config?.timeoutMessage,
                timeoutForMessage: this.#config?.timeoutForMessage,
                timeoutMessage: this.#config?.timeoutMessage
            };
            this.#timeoutMessageComponent = new LoadingAnimationTimeoutMessage(this.#loadingAnimationContainer, timeoutConfig);
        }
        if (this.#config?.spinner?.enabled) {
            const spinnerSize = (this.#capabilities.isIE ? '9%' : '5%');
            const spinnerContainer = this.#utils.DOM.stringToElement(`
				<div style="
					position: relative !important;
					width: ${spinnerSize} !important;
					height: ${spinnerSize} !important;
					min-width: 30px !important;
					min-height: 30px !important;
					max-width: 80px !important;
					max-height: 80px !important;
					z-index: 2;"
					role="alert"
					aria-live="polite"
					aria-label="Loading">
				</div>
			`);
            this.#loadingAnimationContainer?.appendChild(spinnerContainer);
            this.#spinnerComponent = new LoadingAnimationSpinner(this.#SVG_SPINNER_ANIMATION, spinnerContainer, this.#svgAnimationCSSClass);
        }
        this.#fadeInEnabled = this.#config?.fadeIn?.enabled ?? true;
    }
    get name() {
        return 'LoadingAnimationManager';
    }
    get container() {
        return this.#loadingAnimationContainer;
    }
    get backgroundImageURL() {
        return this.#config?.backgroundImage?.imageURL;
    }
    getLoadingAnimation = (container, useInlineCSS = true) => {
        const spinner = new LoadingAnimationSpinner(this.#SVG_SPINNER_ANIMATION, container, this.#svgAnimationCSSClass);
        spinner.show(useInlineCSS);
        return spinner;
    };
    show = (showSpinner = this.#config?.spinner?.enabled, topLevel = false) => {
        if (!this.#enabled) {
            this.hide();
            return;
        }
        if (this.#visible === true) {
            return;
        }
        if (this.#utils.Type.isElement(this.#loadingAnimationContainer) && !this.#utils.Type.isEmpty(this.#loadingAnimationContainer?.style)) {
            if (this.#fadeInEnabled) {
                this.#performFadeIn(topLevel);
            }
            this.#loadingAnimationContainer.style.setProperty('opacity', '1', CSSValue.IMPORTANT);
            this.#loadingAnimationContainer.style.setProperty('display', 'flex', CSSValue.IMPORTANT);
            this.#loadingAnimationContainer.style.setProperty('z-index', topLevel ? '3000' : '100', CSSValue.IMPORTANT);
        }
        if (showSpinner) {
            this.showSpinner();
        }
        if (this.#backgroundImageComponent) {
            this.#backgroundImageComponent.show();
        }
        if (this.#timeoutMessageComponent) {
            this.#timeoutMessageComponent.show();
        }
        this.#visible = true;
    };
    hide = () => {
        if (!this.#visible) {
            return;
        }
        this.hideSpinner();
        if (this.#backgroundImageComponent) {
            this.#backgroundImageComponent.hide();
        }
        if (this.#timeoutMessageComponent) {
            this.#timeoutMessageComponent.hide();
        }
        if (this.#utils.Type.isElement(this.#loadingAnimationContainer) && !this.#utils.Type.isEmpty(this.#loadingAnimationContainer?.style)) {
            this.#loadingAnimationContainer.style.setProperty('display', 'none', CSSValue.IMPORTANT);
        }
        this.#visible = false;
    };
    setBackgroundImage = (imageURL) => {
        if (this.#utils.URL.valid(imageURL)) {
            this.#config.backgroundImage.imageURL = imageURL;
            if (this.#backgroundImageComponent) {
                this.#backgroundImageComponent.setImage(imageURL);
            }
            else {
                this.#backgroundImageComponent = new LoadingAnimationBackgroundImage(this.#loadingAnimationContainer, this.#config.backgroundImage);
            }
        }
    };
    enableBackgroundImage = () => {
        if (this.#backgroundImageComponent) {
            this.#backgroundImageComponent.enable();
        }
    };
    disableBackgroundImage = () => {
        if (this.#backgroundImageComponent) {
            this.#backgroundImageComponent.disable();
        }
    };
    enableSpinner = () => {
        if (this.#spinnerComponent) {
            this.#spinnerComponent.enable();
        }
    };
    disableSpinner = () => {
        if (this.#spinnerComponent) {
            this.#spinnerComponent.disable();
        }
    };
    showSpinner = () => {
        if (this.#spinnerComponent) {
            this.#spinnerComponent.show();
        }
    };
    hideSpinner = () => {
        if (this.#spinnerComponent) {
            this.#spinnerComponent.hide();
        }
    };
    backgroundOpacity = (value) => {
        let bgColor = 'rgba( 0, 0, 0, 1 )';
        if (isNaN(value) === false)
            bgColor = 'rgba( 0, 0, 0, ' + value + ' )';
        if (this.#utils.Type.isElement(this.#loadingAnimationContainer) && !this.#utils.Type.isEmpty(this.#loadingAnimationContainer?.style)) {
            this.#loadingAnimationContainer.style.setProperty('background-color', bgColor, CSSValue.IMPORTANT);
        }
    };
    enableOptimizeMessage = () => {
        if (this.#timeoutMessageComponent) {
            this.#timeoutMessageComponent.enable();
        }
    };
    disableOptimizeMessage = () => {
        if (this.#timeoutMessageComponent) {
            this.#timeoutMessageComponent.disable();
        }
    };
    showOptimizeMessage = () => {
        if (this.#timeoutMessageComponent) {
            this.#timeoutMessageComponent.show();
        }
    };
    hideOptimizeMessage = () => {
        if (this.#timeoutMessageComponent) {
            this.#timeoutMessageComponent.hide();
        }
    };
    enableFadeIn = () => {
        this.#fadeInEnabled = true;
    };
    disableFadeIn = () => {
        this.#fadeInEnabled = false;
    };
    #performFadeIn = (topLevel) => {
        if (this.#utils.Type.isElement(this.#loadingAnimationContainer) && !this.#utils.Type.isEmpty(this.#loadingAnimationContainer?.style)) {
            this.#loadingAnimationContainer.style.setProperty('opacity', '0', CSSValue.IMPORTANT);
            this.#loadingAnimationContainer.style.setProperty('transition', this.#getFadeCSS(this.#config?.fadeIn?.duration), CSSValue.IMPORTANT);
            this.#loadingAnimationContainer.style.setProperty('display', 'flex', CSSValue.IMPORTANT);
            this.#loadingAnimationContainer.style.setProperty('z-index', topLevel ? '3000' : '100', CSSValue.IMPORTANT);
            void this.#loadingAnimationContainer.offsetHeight;
            this.#loadingAnimationContainer.style.setProperty('opacity', '1', CSSValue.IMPORTANT);
            setTimeout(() => {
                this.#loadingAnimationContainer.style.setProperty('transition', 'none', CSSValue.IMPORTANT);
            }, this.#config.fadeIn.duration);
        }
    };
    #getFadeCSS = (duration) => {
        return `opacity ${duration}ms ease-in-out`;
    };
}
